import { useParams, useNavigate, useRouterState } from "@tanstack/react-router";

import { useAuth } from "~/providers/auth.provider";

import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@workspace/web-ui/components/tabs"
// import { Skeleton } from "@workspace/web-ui/components/skeleton"
import { getAge } from "@workspace/lib/date";
import { cn } from "@workspace/web-ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { medicalsMedicationsSummaryQueryOptions } from "../api/queries";
import { Skeleton } from "@workspace/web-ui/components/skeleton";

interface TabNavProps {
  title: string;
  description: string;
  isSupportPage?: boolean;
}

export function TabNavComponent() {
  const { program } = useParams({ strict: false })
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate()

  // get the last part of the pathname
  const lastPart = pathname.split('/').pop()

  return (
    <Tabs defaultValue="weight-loss" className="bg-[#0404040D] rounded-[8888px] gap-2 p-1 w-full max-w-xs sm:max-w-none overflow-x-auto" value={program} onValueChange={(tab) => navigate({
      to: `/dashboard/medicals/$program/${lastPart !== program ? lastPart : ''}`,
      params: { program: tab },
    })}>
      <TabsList className="w-full flex-nowrap overflow-x-auto hide-scrollbar text-sm sm:text-base">
        <TabsTrigger value="weight-loss" className="group gap-2 rounded-[8888px] data-[state=active]:bg-[#FFFFFF] data-[state=active]:text-[#040404] data-[state=active]:border data-[state=active]:border-[#4272DD] whitespace-nowrap">
          <div className={cn("flex size-2 sm:size-3 rounded-full bg-[#04040433] p-[5px]", program === 'weight-loss' && "bg-[#4272DD]")} />
          Show Weightloss data
        </TabsTrigger>
        <TabsTrigger value="urgent-care" className="group gap-2 rounded-[8888px] data-[state=active]:bg-[#FFFFFF] data-[state=active]:text-[#040404] data-[state=active]:border data-[state=active]:border-[#4272DD] whitespace-nowrap">
          <div className={cn("flex size-2 sm:size-3 rounded-full bg-[#04040433] p-[5px]", program === 'urgent-care' && "bg-[#4272DD]")} />
          Show Urgent care data
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )
}

export const TabNav = ({ title, description = "Urgent care and weight loss overview", isSupportPage = false }: TabNavProps) => {
  const { user, authState } = useAuth()
  const { program } = useParams({ strict: false })
  const { data: medicationsSummary, isLoading } = useQuery(medicalsMedicationsSummaryQueryOptions(program === 'weight-loss' ? 'weight_loss' : 'urgent_care'))

  console.log("medicationsSummary", { medicationsSummary, user, authState, program })

  return (
    <div className="flex flex-col items-start gap-3 pb-4 w-full">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full mb-6 gap-4">
        <div className="flex flex-col items-start gap-3 w-full">
          <h1 className="w-full text-2xl sm:text-[40px] font-semibold text-[#1A202C]">
            {title}
          </h1>
          {!isSupportPage && (
            <p className="text-base text-[#464B55]">
              {description}
            </p>
          )}
        </div>

        {/* <div className="w-full sm:w-auto mt-4 sm:mt-0">
          <TabNavComponent />
        </div> */}
      </div>

      {!isSupportPage && (<>
        {(authState !== "authenticated" || !user || isLoading) ? (
          <div className="flex flex-col items-start gap-3 pb-4 w-full">
            <div className="flex flex-col w-full p-4 sm:p-6 gap-3 bg-[#0404040D] mt-4">
              <Skeleton className="h-8 w-40 mb-2" />
              <Skeleton className="h-4 w-64 mb-2" />
              <div className="flex flex-row flex-wrap items-center gap-3">
                <Skeleton className="h-16 w-24 rounded-[8px]" />
                <Skeleton className="h-16 w-24 rounded-[8px]" />
                <Skeleton className="h-16 w-24 rounded-[8px]" />
                <Skeleton className="h-16 w-24 rounded-[8px]" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col w-full p-4 sm:p-6 gap-3 bg-[#0404040D] rounded-[16px]">
            <h2 className="text-xl sm:text-2xl font-semibold text-[#1A202C]">
              {`${user?.first_name} ${user?.last_name}`}
            </h2>
            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
              <h2 className="text-sm" style={{ color: '#2D2F35' }}>
                {program === 'urgent-care' ? 'Urgent Care Program' : 'Weight Loss Program'}
              </h2>
              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                <span className="mx-1" style={{ color: "#667085" }}>•</span>
                <span className="text-sm" style={{ color: '#2D2F35' }}>
                  {getAge(user?.date_of_birth ?? "")} Years
                </span>
                <span className="mx-1" style={{ color: "#667085" }}>•</span>
                <span className="text-sm" style={{ color: '#2D2F35' }}>
                  {user?.gender}
                </span>
              </div>
            </div>

            {program === 'urgent-care' && medicationsSummary?.urgent_care && (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 items-center text-[#202020CC] gap-3">
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Active encounter</h3>
                  <p className="text-base  font-medium">{medicationsSummary?.urgent_care?.active_encounters}</p>
                </div>
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Appointments</h3>
                  <p className="text-base font-medium">{medicationsSummary?.urgent_care?.appointments_count}</p>
                </div>
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Active Meds</h3>
                  <p className="text-base font-medium">{medicationsSummary?.urgent_care?.active_medications_count}</p>
                </div>
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Total encounter</h3>
                  <p className="text-base font-medium">{medicationsSummary?.urgent_care?.total_encounters}</p>
                </div>
              </div>
            )}

            {program === 'weight-loss' && medicationsSummary?.weight_loss && (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 items-center text-[#202020CC] gap-3">
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Initial Weight</h3>
                  <p className="text-base  font-medium">{medicationsSummary?.weight_loss?.initial_weight ?? 0} lbs</p>
                </div>
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Goal Weight</h3>
                  <p className="text-base font-medium">{medicationsSummary?.weight_loss?.goal_weight ?? 0} lbs</p>
                </div>
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Appointments</h3>
                  <p className="text-base font-medium">{medicationsSummary?.weight_loss?.appointments_count}</p>
                </div>
                <div className="flex flex-col items-center justify-center p-3 gap-3 border bg-[#FFFFFF] border-[#0404041A] rounded-[8px] w-full">
                  <h3 className="text-sm">Active Meds</h3>
                  <p className="text-base font-medium">{medicationsSummary?.weight_loss?.active_medications_count}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
};

